import jobServices from 'ApiService/Placement/jobServices';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BsXCircleFill } from 'react-icons/bs';
import { MdOutlineCloudUpload } from 'react-icons/md';
import { AiOutlineDelete } from 'react-icons/ai';
import React from 'react';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const Modal = ({ isOpen, onClose, file }) => {
  if (!isOpen || !file) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-75">
      <div className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-md bg-white shadow-lg dark:bg-navy-700">
        <button
          onClick={onClose}
          className="absolute right-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-3xl text-white"
        >
          &times;
        </button>
        <div className="max-h-[90vh] w-full overflow-auto p-1">
          {file.type === 'image' && (
            <img
              src={file.src}
              alt="Preview"
              className="mx-auto max-h-full max-w-full rounded-md object-contain"
            />
          )}
          {file.type === 'pdf' && (
            <embed
              src={file.src}
              width="100%"
              height="600px"
              type="application/pdf"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Attachments = ({ selectedStudent, handleBackClick }) => {
  const [newFiles, setNewFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshFiles, setRefreshFiles] = useState(false);
  const [modalFile, setModalFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    fileId: null,
    isNewFile: false,
    index: -1,
  });
  const [uploadConfirmation, setUploadConfirmation] = useState({
    isOpen: false,
  });

  const allowedExtensions = ['jpeg', 'png', 'jpg', 'pdf', 'docx'];
  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL.replace(
    '/api/v1',
    ''
  )}`;

  const handleFileChange = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        toast.error(
          `Invalid file format. Allowed: ${allowedExtensions.join(', ')}`
        );
        return;
      }

      if (file.size > 20 * 1024 * 1024) {
        toast.error('File size exceeds 20 MB limit');
        return;
      }

      setNewFiles([...newFiles, file]);
      toast.success('File selected successfully!');
    }
  };

  const confirmRemoveNewFile = (index) => {
    setDeleteConfirmation({
      isOpen: true,
      fileId: null,
      isNewFile: true,
      index: index,
    });
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles(newFiles.filter((_, i) => i !== index));
    setDeleteConfirmation({
      isOpen: false,
      fileId: null,
      isNewFile: false,
      index: -1,
    });
  };

  const confirmRemoveExistingFile = (fileId) => {
    setDeleteConfirmation({
      isOpen: true,
      fileId: fileId,
      isNewFile: false,
      index: -1,
    });
  };

  const handleRemoveExistingFile = async (fileId) => {
    try {
      setDeleteConfirmation({
        isOpen: false,
        fileId: null,
        isNewFile: false,
        index: -1,
      });

      const response = await jobServices.deleteAttachment(
        fileId,
        selectedStudent._id
      );
      if (response.data.success) {
        setExistingFiles(existingFiles.filter((file) => file._id !== fileId));
        toast.success('File deleted successfully');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleFileClick = (file, isNewFile = false) => {
    const fileExtension = isNewFile
      ? file.name.split('.').pop().toLowerCase()
      : file.fileType.toLowerCase();

    const fileUrl = isNewFile
      ? URL.createObjectURL(file)
      : `${BASE_URL}/${file.filepath}`;

    if (['jpg', 'jpeg', 'png', 'pdf'].includes(fileExtension)) {
      setModalFile({
        src: fileUrl,
        type: ['jpg', 'jpeg', 'png'].includes(fileExtension) ? 'image' : 'pdf',
      });
      setIsModalOpen(true);
    }
  };

  const fetchExistingFiles = async () => {
    try {
      const response = await jobServices.getAttachments(selectedStudent._id);
      if (response.data.success) {
        setExistingFiles(response.data.data || []);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch attachments.'
      );
    }
  };

  const confirmUpload = () => {
    if (newFiles.length === 0) {
      toast.error('No new files selected for upload.');
      return;
    }

    setUploadConfirmation({
      isOpen: true,
    });
  };

  const handleSubmit = async () => {
    try {
      setUploadConfirmation({ isOpen: false });

      if (newFiles.length === 0) {
        toast.error('No new files selected for upload.');
        return;
      }

      setLoading(true);

      for (const file of newFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('studentId', selectedStudent._id);

        const response = await jobServices.uploadAttachment(formData);
        if (!response?.data?.success) {
          throw new Error(response?.data?.message || 'Failed to upload file');
        }
      }

      toast.success('All files uploaded successfully');
      setNewFiles([]);
      await fetchExistingFiles();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      toast.error('Error uploading files: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStudent?._id) {
      fetchExistingFiles();
    }
  }, [selectedStudent._id, refreshFiles]);

  const renderNewFilePreview = (file, index) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png'].includes(fileExtension);

    return (
      <div key={`new-${index}`} className="relative mb-4">
        <div className="flex items-center rounded-lg border p-4">
          <div
            className="cursor-pointer"
            onClick={() => handleFileClick(file, true)}
          >
            {isImage ? (
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="h-20 w-20 object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center bg-gray-100">
                <span className="text-sm">{fileExtension.toUpperCase()}</span>
              </div>
            )}
          </div>
          <div className="ml-4 flex-grow">
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-gray-500">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <button
            onClick={() => confirmRemoveNewFile(index)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <BsXCircleFill size={20} />
          </button>
        </div>
      </div>
    );
  };

  const renderExistingFilePreview = (file) => {
    const isImage = ['jpg', 'jpeg', 'png'].includes(
      file.fileType.toLowerCase()
    );
    const fileUrl = `${BASE_URL}/${file.filepath}`;

    return (
      <div key={file._id} className="relative mb-4">
        <div className="flex items-center rounded-lg border p-4">
          <div className="cursor-pointer" onClick={() => handleFileClick(file)}>
            {isImage ? (
              <img
                src={fileUrl}
                alt={file.filename}
                className="h-20 w-20 object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center bg-gray-100">
                <span className="text-sm">{file.fileType.toUpperCase()}</span>
              </div>
            )}
          </div>
          <div className="ml-4 flex-grow">
            <p className="font-medium">{file.filename}</p>
            <p className="text-sm text-gray-500">
              Uploaded on: {new Date(file.submittedOn).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={() => confirmRemoveExistingFile(file._id)}
            className="ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-700"
          >
            <AiOutlineDelete size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-3 h-full w-full">
      <div className="md:2/3 mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
        <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
          <h4 className="mb-6 pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
            Upload Attachments
          </h4>

          <div className="mb-6">
            <div className="relative flex h-[225px] w-full flex-col items-center justify-center rounded-xl border-[1px] border-dashed border-gray-200 bg-gray-100 dark:border-none dark:bg-navy-700">
              <input
                type="file"
                onChange={(e) => handleFileChange(e.target.files)}
                className="absolute h-full w-full cursor-pointer opacity-0"
                accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
              />
              <div className="flex items-center justify-center">
                <MdOutlineCloudUpload className="text-[80px] text-brand-500 dark:text-white" />
              </div>
              <p className="text-lg font-bold text-navy-700 dark:text-white">
                Drop your files here, or browse
              </p>
            </div>
          </div>

          <div className="mt-6">
            <h5 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              New Attachment to Upload
            </h5>
            {newFiles.length > 0 ? (
              newFiles.map((file, index) => renderNewFilePreview(file, index))
            ) : (
              <p className="text-center text-gray-500">No new files selected</p>
            )}
            <div className="mt-8 flex justify-center">
              <button
                onClick={confirmUpload}
                className="rounded-xl bg-brand-500 px-8 py-2 text-white"
                disabled={newFiles.length === 0 || loading}
              >
                {loading ? 'Uploading...' : 'Upload New Attachment'}
              </button>
            </div>
          </div>

          <div className="mt-6">
            <h5 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              Existing Files
            </h5>
            {existingFiles.length > 0 ? (
              existingFiles.map((file) => renderExistingFilePreview(file))
            ) : (
              <p className="text-center text-gray-500">No existing files</p>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        file={modalFile}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() =>
          setDeleteConfirmation({ ...deleteConfirmation, isOpen: false })
        }
        onConfirm={() => {
          if (deleteConfirmation.isNewFile) {
            handleRemoveNewFile(deleteConfirmation.index);
          } else {
            handleRemoveExistingFile(deleteConfirmation.fileId);
          }
        }}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this file? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        icon="warning"
      />

      <ConfirmationModal
        isOpen={uploadConfirmation.isOpen}
        onClose={() => setUploadConfirmation({ isOpen: false })}
        onConfirm={handleSubmit}
        title="Confirm Upload"
        message={`Are you sure you want to upload ${newFiles.length} file(s)?`}
        confirmText="Upload"
        cancelText="Cancel"
        icon="info"
      />
    </div>
  );
};

export default Attachments;
